import { useEffect, useState } from "react";
import axios from "axios";

// Same backend resolution as the rest of the funnel (live site uses its own origin).
const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
export const IE_EXPERIMENT_KEY = "ie_headline_v1";
const COOKIE = `sz_exp_${IE_EXPERIMENT_KEY}`;
const CFG_CACHE = `sz_exp_cfg_${IE_EXPERIMENT_KEY}`;
const SESSION_STATE = `sz_exp_sess_${IE_EXPERIMENT_KEY}`;
const VID = "sz_vid";
const DEADLINE_MS = 300;
export const CONTROL_VARIANT = { id: "A", headline: "What do you need help with today?", subhead: "" };

// Built-in copy of the seeded config, used only when the live config is slow and nothing is cached.
const BUILT_IN = {
  enabled: true,
  variants: [
    { ...CONTROL_VARIANT, weight: 25 },
    { id: "B", headline: "See your screen repair price in about a minute", subhead: "Pick what you need, answer a few quick questions, and get your instant quote. No phone call required.", weight: 25 },
    { id: "C", headline: "Mobile window screen repair, we come to you", subhead: "Serving Murrieta, Temecula, Menifee, Wildomar, Lake Elsinore, Winchester & French Valley. Start your instant quote below.", weight: 25 },
    { id: "D", headline: "Torn, bent or missing screens? Get an instant quote.", subhead: "Rescreens, new screens, sliding screen doors, PetScreen and solar screens, done at your home.", weight: 25 },
  ],
};

const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
};
const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode / in-app browser */ } },
};
const persisted = (name) => readCookie(name) || ls.get(name);
const persist = (name, value) => {
  try { document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${365 * 86400}; path=/; SameSite=Lax`; } catch { /* ignore */ }
  ls.set(name, value);
};

const pickByWeight = (variants) => {
  const pool = variants.filter((v) => Number(v.weight) > 0);
  const total = pool.reduce((s, v) => s + Number(v.weight), 0);
  let r = Math.random() * total;
  return pool.find((v) => (r -= Number(v.weight)) < 0) || pool[pool.length - 1];
};

const cachedConfig = () => {
  try { return JSON.parse(ls.get(CFG_CACHE)) || null; } catch { return null; }
};

const visitorId = () => {
  let id = persisted(VID);
  if (!id) id = `v_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  persist(VID, id);
  return id;
};

// Staff = this browser has (or had) a CRM login.
const isStaffBrowser = () => {
  if (ls.get("session_token")) ls.set("sz_staff", "1");
  return !!ls.get("sz_staff");
};

const FUNNEL_PATHS = ["/instant-estimate", "/quote"];
let prefetch = null;
const startFetch = () => axios.get(`${API}/public/experiments/${IE_EXPERIMENT_KEY}`, { timeout: 8000 })
  .then((r) => { ls.set(CFG_CACHE, JSON.stringify(r.data)); return r.data; });
if (typeof window !== "undefined" && FUNNEL_PATHS.some((p) => window.location.pathname.startsWith(p))) {
  prefetch = startFetch();
  prefetch.catch(() => {});
}
const fetchConfig = () => { const p = prefetch || startFetch(); prefetch = null; return p; };

// Assign (or reuse) the sticky variant from a config. Returns the finished state.
const decide = (cfg, source, forced) => {
  const variants = cfg?.variants || [];
  const forcedV = forced && variants.find((v) => v.id === forced);
  if (forcedV) return { variant: forcedV, inExperiment: true, qa: true, status: "qa" };
  if (!cfg?.enabled) return { variant: CONTROL_VARIANT, inExperiment: false, status: `disabled${source}` };
  const v = variants.find((x) => x.id === persisted(COOKIE)) || pickByWeight(variants);
  if (!v) return { variant: CONTROL_VARIANT, inExperiment: false, status: `no_weighted_variant${source}` };
  persist(COOKIE, v.id);
  return { variant: v, inExperiment: true, status: `assigned${source}` };
};

const readSession = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_STATE)) || null; } catch { return null; }
};
const saveSession = (s) => { try { sessionStorage.setItem(SESSION_STATE, JSON.stringify(s)); } catch { /* ignore */ } };

// Sticky per-visitor headline assignment. If the live config is slower than 300 ms (or fails),
// the visitor still gets their assigned headline from the last known (or built-in) settings.
export function useHeadlineExperiment() {
  const [state, setState] = useState({ ready: false, variant: CONTROL_VARIANT, inExperiment: false, qa: false });

  useEffect(() => {
    let done = false;
    const forced = (new URLSearchParams(window.location.search).get("sz_variant") || "").toUpperCase();
    const finish = (s) => {
      if (done) return;
      done = true;
      const next = { ready: true, qa: false, ...s };
      if (!forced) saveSession({ variant: next.variant, inExperiment: next.inExperiment, status: next.status });
      setState(next);
    };
    const fallback = (why) => {
      // Same session already resolved: reuse it so a slow load never re-rolls or drops to A mid-session.
      const sess = !forced && readSession();
      if (sess?.variant) return finish({ ...sess, status: sess.inExperiment ? `assigned_session_${why}` : sess.status });
      const cached = cachedConfig();
      finish(decide(cached || BUILT_IN, cached ? `_cached_${why}` : `_builtin_${why}`, forced));
    };
    const timer = setTimeout(() => fallback("timeout"), DEADLINE_MS);
    fetchConfig()
      .then((cfg) => finish(decide(cfg, "", forced)))
      .catch(() => fallback("error"))
      .finally(() => clearTimeout(timer));
    return () => clearTimeout(timer);
  }, []);

  const [vid] = useState(visitorId);
  const [staff] = useState(isStaffBrowser);
  const on = state.inExperiment;
  return {
    ...state,
    eventFields: on
      ? { experiment_key: IE_EXPERIMENT_KEY, headline_variant: state.variant.id, visitor_id: vid, qa: state.qa, staff, exp_status: state.status }
      : { visitor_id: vid, staff, exp_status: state.status },
    leadFields: on
      ? { experiment_key: IE_EXPERIMENT_KEY, headline_variant: state.variant.id, exp_visitor_id: vid, exp_excluded: state.qa || staff }
      : { exp_visitor_id: vid },
  };
}
